
/**
 * Module dependencies.
 */

import { Cycle } from 'src/types/company-page';
import {
  RawHtml,
  Type,
  color,
  media,
  normalizeNewLine,
  units
} from '@untile/react-components';

import { Subtitle } from '../core/typography';
import { theme } from 'styled-tools';
import { useInterval } from 'src/hooks/use-interval';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image from 'src/components/core/image';
import React, { Fragment, ReactElement, useRef } from 'react';
import map from 'lodash/map';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  canTrigger: boolean,
  className?: string,
  data: Cycle[],
  isAnimated: boolean,
  isVisible: boolean,
  title: string
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  height: calc(100vh - ${theme('dimensions.navbarHeightMobile')}px);
  position: relative;

  ${media.min('md')`
    height: calc(100vh - ${theme('dimensions.navbarHeight')}px);
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas: 'title .';
  grid-template-columns: 4fr 8fr;
  grid-template-rows: 100%;
  height: 100%;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1).attrs({ as: 'h2' })`
  grid-area: title;
  pointer-events: none;
  position: absolute;
  text-transform: lowercase;

  ${media.min('ms')`
    padding-top: 10vw;
  `}

  ${media.min('md')`
    padding-top: 0;
  `}
`;

/**
 * `CycleImage` styled component.
 */

const CycleImage = styled.div`
  border: ${units(1)} solid transparent;
  border-radius: 50%;
  height: 40vw;
  position: relative;
  transition: ${theme('animations.defaultTransition')};
  width: 40vw;

  ${media.min('md')`
    height: ${units(32)};
    width: ${units(32)};
  `}

  &::before {
    background-color: transparent;
    border: 6px solid transparent;
    border-radius: 50%;
    content: '';
    height: calc(100% + ${units(1.5)});
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: calc(100% + ${units(1.5)});
  }
`;

/**
 * `Label` styled component.
 */

const Label = styled(Subtitle).attrs({ size: 'large' })`
  opacity: 0;
  text-align: center;
  text-transform: uppercase;
  transition: opacity ${theme('animations.defaultTransition')};
`;

/**
 * `CycleSlider` styled component.
 */

const CycleSlider = styled.div`
  display: flex;
  height: 100%;
  justify-content: center;
  perspective: 1000px;
  position: relative;
  width: 100%;

  input {
    display: none;
  }

  #cycle-input-0:checked ~ #cycle-item-0,
  #cycle-input-1:checked ~ #cycle-item-1,
  #cycle-input-2:checked ~ #cycle-item-2,
  #cycle-input-3:checked ~ #cycle-item-3,
  #cycle-input-4:checked ~ #cycle-item-4,
  #cycle-input-5:checked ~ #cycle-item-5 {
    transform: translate3d(0, 120%, 0);
    z-index: 4;

    ${media.min('md')`
      transform: translate3d(0, 80%, 0);
    `}

    ${media.min('xxl')`
      transform: translate3d(0, 90%, 0);
    `}

    @media only screen and (min-width: 2500px) {
      transform: translate3d(0, 110%, 0);
    }

    ${CycleImage} {
      filter: blur(0) grayscale(0);
      
      &::before {
        border-color: ${color('#bfe1df')};
      }
    }

    ${Label} {
      opacity: 1;
    }
  }

  #cycle-input-0:checked ~ #cycle-item-3,
  #cycle-input-1:checked ~ #cycle-item-4,
  #cycle-input-2:checked ~ #cycle-item-5,
  #cycle-input-3:checked ~ #cycle-item-0,
  #cycle-input-4:checked ~ #cycle-item-1,
  #cycle-input-5:checked ~ #cycle-item-2 {
    pointer-events: none;
    transform: translate3d(0, 20%, -800px);
    z-index: 1;

    ${media.min('md')`
      transform: translate3d(0, -40%, -800px);
    `}

    ${CycleImage} {
      filter: blur(8px) grayscale(1);
    }
  }

  #cycle-input-0:checked ~ #cycle-item-1,
  #cycle-input-1:checked ~ #cycle-item-2,
  #cycle-input-2:checked ~ #cycle-item-3,
  #cycle-input-3:checked ~ #cycle-item-4,
  #cycle-input-4:checked ~ #cycle-item-5,
  #cycle-input-5:checked ~ #cycle-item-0 {
    transform: translate3d(-42vw, 105%, -300px);
    z-index: 3;

    ${media.min('md')`
      transform: translate3d(-240px, 55%, -300px);
    `}

    @media only screen and (min-width: 2500px) {
      transform: translate3d(-240px, 65%, -300px);
    }

    ${CycleImage} {
      filter: blur(2px) grayscale(1);
    }
  }

  #cycle-input-0:checked ~ #cycle-item-2,
  #cycle-input-1:checked ~ #cycle-item-3,
  #cycle-input-2:checked ~ #cycle-item-4,
  #cycle-input-3:checked ~ #cycle-item-5,
  #cycle-input-4:checked ~ #cycle-item-0,
  #cycle-input-5:checked ~ #cycle-item-1 {
    pointer-events: none;
    transform: translate3d(-30vw, 55%, -700px);
    z-index: 2;

    ${media.min('md')`
      transform: translate3d(-200px, -10%, -700px);
    `}

    ${CycleImage} {
      filter: blur(5px) grayscale(1);
    }
  }

  #cycle-input-0:checked ~ #cycle-item-4,
  #cycle-input-1:checked ~ #cycle-item-5,
  #cycle-input-2:checked ~ #cycle-item-0,
  #cycle-input-3:checked ~ #cycle-item-1,
  #cycle-input-4:checked ~ #cycle-item-2,
  #cycle-input-5:checked ~ #cycle-item-3 {
    pointer-events: none;
    transform: translate3d(30vw, 55%, -700px);
    z-index: 2;

    ${media.min('md')`
      transform: translate3d(200px, -10%, -700px);
    `}

    ${CycleImage} {
      filter: blur(5px) grayscale(1);
    }
  }

  #cycle-input-0:checked ~ #cycle-item-5,
  #cycle-input-1:checked ~ #cycle-item-0,
  #cycle-input-2:checked ~ #cycle-item-1,
  #cycle-input-3:checked ~ #cycle-item-2,
  #cycle-input-4:checked ~ #cycle-item-3,
  #cycle-input-5:checked ~ #cycle-item-4 {
    transform: translate3d(42vw, 105%, -300px);
    z-index: 3;

    ${media.min('md')`
      transform: translate3d(240px, 55%, -300px);
    `}

    @media only screen and (min-width: 2500px) {
      transform: translate3d(240px, 65%, -300px);
    }

    ${CycleImage} {
      filter: blur(2px) grayscale(1);
    }
  }
`;

/**
 * `CycleItem` styled component.
 */

const CycleItem = styled.label`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  grid-gap: ${units(4)};
  position: absolute;
  transition: transform ${theme('animations.defaultTransition')};

  /* stylelint-disable no-descending-specificity */
  &:hover {
    ${CycleImage} {
      filter: blur(0) grayscale(0) !important;
    }
  }
  /* stylelint-enable no-descending-specificity */
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  overflow: hidden;
  padding-bottom: 100%;
  position: relative;
`;

/**
 * `CycleSection` component.
 */

const CycleSection = (props: Props): ReactElement => {
  const { canTrigger, className, data, isAnimated, isVisible, title } = props;
  const refs = useRef<HTMLInputElement[]>([]);
  const { resetInterval } = useInterval(() => {
    const total = refs.current.length;

    if (total <= 1) {
      return;
    }

    for (let index = 0; index < total; index++) {
      const ref = refs.current[index];

      if (ref.checked === true) {
        if (index === 0) {
          refs.current[total - 1].click();

          break;
        }

        refs.current[index - 1].click();

        break;
      }
    }
  }, 2000);

  return (
    <Wrapper className={className}>
      <Container>
        <Grid>
          <FadeInUpAnimation
            canTrigger={canTrigger}
            isAnimated={isAnimated}
            isVisible={isVisible}
            options={{
              distance: '25vh',
              transitionDelay: 0.5,
              transitionTime: 1
            }}
          >
            <RawHtml element={Title}>
              {normalizeNewLine(title)}
            </RawHtml>
          </FadeInUpAnimation>
        </Grid>
      </Container>

      <FadeInUpAnimation
        canTrigger={canTrigger}
        className={'full-height'}
        isAnimated={isAnimated}
        isVisible={isVisible}
        options={{
          distance: '25%',
          transitionDelay: 1,
          transitionTime: 1
        }}
      >
        <CycleSlider>
          {map(data, ({ label }: Cycle, index: number) => (
            <Fragment key={index}>
              <input
                defaultChecked={index === 0}
                id={`cycle-input-${index}`}
                key={label}
                name={'cycle-carousel'}
                onChange={() => resetInterval()}
                ref={el => { refs.current[index] = el; }}
                type={'radio'}
              />
            </Fragment>
          ))}

          {map(data, ({ image, label }: Cycle, index: number) => (
            <CycleItem
              htmlFor={`cycle-input-${index}`}
              id={`cycle-item-${index}`}
              key={index}
            >
              <CycleImage>
                <ImageWrapper>
                  <Image
                    alt={label}
                    layout={'fill'}
                    objectFit={'cover'}
                    src={image}
                  />
                </ImageWrapper>
              </CycleImage>

              <Label>
                {label}
              </Label>
            </CycleItem>
          ))}
        </CycleSlider>
      </FadeInUpAnimation>
    </Wrapper>
  );
};

/**
 * Export `CycleSection` component.
 */

export default CycleSection;
